import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader, Stack, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';

import {
  useActivateMutation,
  useVerifyActivationTokenQuery,
} from '@api/users.api';

interface ActivateUserProps {
  token: string;
  onSuccess?: () => void;
}

export default function ActivateUser({ token, onSuccess }: ActivateUserProps) {
  const { t } = useTranslation();

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data, isLoading, error } = useVerifyActivationTokenQuery(token!);
  const [activateUser, { isLoading: isLoadingActivate }] =
    useActivateMutation();

  useEffect(() => {
    const f = async () => {
      if (!error && data) {
        try {
          await activateUser(token!).unwrap();

          onSuccess?.();
        } catch (e) {
          console.error(e);
          showNotification({
            color: 'red',
            title: t('userAccount.error'),
            message: t('activation.activationError'),
          });
        }
      }
    };

    f();
  }, [data, error, activateUser, token]);

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Stack mt="xl" align="center" maw="50rem">
      {isLoading || isLoadingActivate ? (
        <Loader />
      ) : error ? (
        <>
          <Text ta="center" fw="bold">
            {t('activation.invalidToken')}
          </Text>
        </>
      ) : (
        <Text ta="center" fw="bold">
          {t('activation.accountVerified')}
        </Text>
      )}
    </Stack>
  );
}
